/**
 * KitRecordPage -- the RECORD-PAGE archetype's 3-zone composition shell
 * (masthead / center / rail) with the FROZEN column shares.
 *
 * Law homes (cited, never re-derived here):
 *  - RECORD-PAGE archetype (docs/design/design-language/07-BEHAVIOURAL-LAWS.md
 *    "### ARCH", UPDATE 2): the standing shape every future record page
 *    inherits by citation -- masthead -> lifecycle line -> NEXT band ->
 *    timeline-dominant center -> counted expand-in-place rail, in that fixed
 *    order. This shell renders the order and the two-column grid so an adopter
 *    (Patient 360, the COC opportunity record, Money detail) assembles from
 *    the proven parts instead of interpreting a mockup.
 *  - FIVE-SECOND LAW (07 "### 5SEC"): the first screen is the whole story; the
 *    always-visible zones (masthead, lifecycle, NEXT band) sit above the fold.
 *  - ANNOTATION-LAYER RIDER (07 "### ARCH" UPDATE 2): mockup annotation boxes
 *    are instruction-layer and are NEVER rendered as UI.
 *
 * FROZEN COLUMN SHARES: the two-column grid is the archetype's own CSS
 * (`.lead-record__grid { grid-template-columns: minmax(0, 1fr) 340px }` in
 * dashboard.css) -- measured on the approved chronicle-normal frame
 * (timeline share ~0.723 / rail share ~0.264; see
 * tools/visual-judge/anatomy/record-page.anatomy.json). Adopters reuse the
 * SAME grid class so the shares stay frozen, not re-guessed per surface.
 *
 * PURE SHELL: every zone arrives as a slot (ReactNode) from the caller -- this
 * component fetches nothing and derives nothing. The masthead slot is expected
 * to be a KitRecordMasthead (which itself mounts the stage rail as zone 2); the
 * optional aboveNext / belowNext slots carry per-surface seams (e.g. an overdue
 * alert strip, a residual status band) without breaking the archetype order.
 */
import type { ReactNode } from 'react';

export interface KitRecordPageProps {
  /**
   * The outer record-scope className. Defaults to the archetype base
   * `lead-record`; a surface passes its own scope classes (e.g. the Lead
   * record's `lead-detail lead-record lead-record--b`).
   */
  readonly className?: string;
  /** Optional data-testid on the outer section (omitted => no attribute). */
  readonly testId?: string;
  /** Above-archetype row: breadcrumbs + back link (rendered before zone 1). */
  readonly top?: ReactNode;
  /** ZONES 1-2: the masthead (KitRecordMasthead), which mounts the stage rail. */
  readonly masthead: ReactNode;
  /** Optional seam directly above the NEXT band (e.g. an overdue alert strip). */
  readonly aboveNext?: ReactNode;
  /** ZONE 3: the NEXT band (KitNextBand). */
  readonly next?: ReactNode;
  /** Optional content directly below the NEXT band (e.g. a residual status band). */
  readonly belowNext?: ReactNode;
  /** ZONE 4: the timeline-dominant center column content (KitTimeline). */
  readonly center: ReactNode;
  /** ZONE 5: the counted rail sections (KitRailSection[]), in the surface's own order. */
  readonly rail: ReactNode;
  /** data-testid on the grid container (archetype default). */
  readonly gridTestId?: string;
  /** data-testid on the center-zones container (archetype default). */
  readonly zonesTestId?: string;
  /** The rail <aside> className (the `.lead-rail`-scoped ALL-CAPS styling home). */
  readonly railClassName?: string;
  /** The rail <aside> aria-label. */
  readonly railAriaLabel?: string;
  /** data-testid on the rail <aside> (archetype default). */
  readonly railTestId?: string;
}

export default function KitRecordPage({
  className = 'lead-record',
  testId,
  top,
  masthead,
  aboveNext,
  next,
  belowNext,
  center,
  rail,
  gridTestId = 'lead-record-grid',
  zonesTestId = 'record360-zones',
  railClassName = 'lead-rail',
  railAriaLabel = 'Related information',
  railTestId = 'lead-record-rail',
}: KitRecordPageProps): JSX.Element {
  return (
    <section className={className} data-testid={testId}>
      {top}
      {masthead}
      {aboveNext}
      {next}
      {belowNext}
      <div className="lead-record__grid" data-testid={gridTestId}>
        <div className="lead-record__main">
          <div className="record360-zones" data-testid={zonesTestId}>
            {center}
          </div>
        </div>
        <aside
          className={railClassName}
          aria-label={railAriaLabel}
          data-testid={railTestId}
        >
          {rail}
        </aside>
      </div>
    </section>
  );
}
